import qs from 'qs';

import { COMMIT_MESSAGE_LENGTH } from 'src/redux/commit-list';
import { ISO8601DateString, UrlPieces } from 'src/types/pull-request';
import { ContextLineRanges } from 'src/utils/validate-context-lines';

import { qsDefaultOptions, buildCommentUrl } from './url-utils';

const baseUrl = '!api/internal/repositories';
const DEFAULT_ACTIVITY_PAGELEN = 50;

type PaginatedUrlPieces = UrlPieces & { pagelen?: number };

type ActivityUrlPieces = PaginatedUrlPieces & {
  /** When true (pr-comments-skip-pr-field), omit embedded PR payloads from activity values. */
  omitPullrequestActivityFields?: boolean;
};

export const internal = {
  commits: (
    owner: string,
    slug: string,
    id: string | number,
    pagelen?: string
  ) => {
    const url = `/${baseUrl}/${owner}/${slug}/pullrequests/${id}/commits`;
    const queryParams = {
      truncate_to: COMMIT_MESSAGE_LENGTH,
      fields: '+*.committer.*,+*.participants.approved,-*.participants.*',
      ...(pagelen && { pagelen }),
    };

    const query = qs.stringify(queryParams, qsDefaultOptions);
    return `${url}${query}`;
  },

  activity: ({
    owner,
    slug,
    id,
    pagelen = DEFAULT_ACTIVITY_PAGELEN,
    omitPullrequestActivityFields = false,
  }: ActivityUrlPieces) => {
    const fields = [
      '+values.attachment.uuid',
      '+values.update.source.commit.parents.hash',
      // Exclude comment content from activity feed to reduce payload size, we
      // load comments separately so this is unused data; it also saves time
      // trying to render/linkify each comment. Note that some parts of
      // `values.comment` _are_ used to help order other activity feed items, but
      // we don't need the content (which is the expensive part).
      '-values.comment.content',
    ];
    if (omitPullrequestActivityFields) {
      fields.push(
        '-values.pull_request',
        '-values.comment.pullrequest',
        '-values.changes_requested.pullrequest',
        '-values.approval.pullrequest'
      );
    }
    const qsParams = {
      pagelen,
      fields: fields.join(','),
    };
    const query = qs.stringify(qsParams, qsDefaultOptions);
    const url = `/${baseUrl}/${owner}/${slug}/pullrequests/${id}/activity`;

    return `${url}${query}`;
  },

  orderedCommentsFetch: (
    user: string,
    repoSlug: string,
    pullRequestId: string | number,
    omitPullrequestField = false
  ) => {
    const baseFields =
      '+values.user.account_status,+values.resolution.*,+values.inline.*';
    // the PR comments API serializes the PR object for every PR comment
    // we do not need this data in the FE so we can omit it and reduce API latency
    const fields = omitPullrequestField
      ? `${baseFields},-values.pullrequest`
      : baseFields;
    return buildCommentUrl(user, repoSlug, pullRequestId, undefined, {
      baseUrl,
      extraQueryStringParams: {
        fields,
        pagelen: 100,
        sort: 'id',
      },
    });
  },

  conflicts: (user: string, repoSlug: string, pullRequestId: string | number) =>
    `/!api/internal/repositories/${user}/${repoSlug}/pullrequests/${pullRequestId}/conflicts`,

  contextFromSrcTopicDiff: (
    pieces: {
      owner: string;
      slug: string;
      sourceHash: string;
      filepath: string;
    },
    lineNums: ContextLineRanges
  ) => {
    const lineNumParams = {
      fromline: lineNums.startingTo,
      toline: lineNums.endingTo,
    };

    const { owner, slug, sourceHash, filepath } = pieces;
    const url = `/!api/internal/repositories/${owner}/${slug}/src/${sourceHash}/${encodeURIComponent(
      filepath
    )}`;

    const query = qs.stringify(lineNumParams, {
      addQueryPrefix: true,
      skipNulls: true,
    });

    return `${url}${query}`;
  },

  imageUpload: (owner: string, slug: string) =>
    `/xhr/${owner}/${slug}/image-upload/`,

  mergeChecks: (
    user: string,
    repoSlug: string,
    pullRequestId: string | number
  ) =>
    `/!api/internal/repositories/${user}/${repoSlug}/pullrequests/${pullRequestId}/merge-restrictions`,

  pendingMerge: (
    owner: string,
    repoSlug: string,
    pullRequestId: string | number
  ) =>
    `/!api/internal/repositories/${owner}/${repoSlug}/pullrequests/${pullRequestId}/post-build-merge`,

  queue: (owner: string, repoSlug: string, pullRequestId: string | number) =>
    `/!api/internal/repositories/${owner}/${repoSlug}/pullrequests/${pullRequestId}/queue`,

  watch: (owner: string, repoSlug: string, pullRequestId: string | number) =>
    `/!api/internal/repositories/${owner}/${repoSlug}/pullrequests/${pullRequestId}/watch`,

  commentById: (
    owner: string,
    repoSlug: string,
    pullRequestId: string | number,
    commentId: string | number
  ) =>
    `/!api/internal/repositories/${owner}/${repoSlug}/pullrequests/${pullRequestId}/comments/${commentId}`,

  likeComment: (
    owner: string,
    repoSlug: string,
    pullRequestId: string | number,
    commentId: string | number
  ) =>
    `/!api/internal/repositories/${owner}/${repoSlug}/pullrequests/${pullRequestId}/comments/${commentId}/likes`,

  commentLikes: (
    owner: string,
    repoSlug: string,
    pullRequestId: string | number
  ) =>
    `/!api/internal/repositories/${owner}/${repoSlug}/pullrequests/${pullRequestId}/likes`,

  updates: (
    owner: string,
    repoSlug: string,
    pullRequestId: string | number,
    since: Date | ISO8601DateString
  ) => {
    const sinceVal = typeof since === 'string' ? since : since.toISOString();
    return `/!api/internal/repositories/${owner}/${repoSlug}/pullrequests/${pullRequestId}/updates?since=${encodeURIComponent(
      sinceVal
    )}`;
  },

  branchSyncInfo: (
    owner: string,
    repoSlug: string,
    pullRequestId: string | number
  ) =>
    `/!api/internal/repositories/${owner}/${repoSlug}/pullrequests/${pullRequestId}/branch-sync-info`,

  revert: (owner: string, repoSlug: string, pullRequestId: string | number) =>
    `/!api/internal/repositories/${owner}/${repoSlug}/pullrequests/${pullRequestId}/revert`,

  updateReviewer: (
    owner: string,
    repoSlug: string,
    pullRequestId: string | number,
    userAaid: string
  ) =>
    `/!api/internal/repositories/${owner}/${repoSlug}/pullrequests/${pullRequestId}/reviewers/${userAaid}`,

  updateReviewGroup: (
    owner: string,
    repoSlug: string,
    pullRequestId: string | number,
    groupId: string
  ) =>
    `/!api/internal/repositories/${owner}/${repoSlug}/pullrequests/${pullRequestId}/reviewers/groups/${groupId}`,

  updatePullRequestSourceBranch: (
    owner: string,
    repoSlug: string,
    pullRequestId: string | number
  ) =>
    `/!api/internal/repositories/${owner}/${repoSlug}/pullrequests/${pullRequestId}/update-source-branch`,

  // multi-comment review endpoints
  review: (owner: string, repoSlug: string, pullRequestId: string | number) =>
    `/!api/internal/repositories/${owner}/${repoSlug}/pullrequests/${pullRequestId}/review`,
  // returns list of pending reviewers for a PR
  pendingReviews: (
    owner: string,
    repoSlug: string,
    pullRequestId: string | number
  ): string =>
    `/!api/internal/repositories/${owner}/${repoSlug}/pullrequests/${pullRequestId}/pendingreviews`,

  // pr review groups
  reviewGroups: (
    owner: string,
    repoSlug: string,
    pullRequestId: string | number
  ): string =>
    `/!api/internal/repositories/${owner}/${repoSlug}/pullrequests/${pullRequestId}/reviewers/groups`,

  // pull request labels list
  prLabels: (
    owner: string,
    repoSlug: string,
    pullRequestId: string | number
  ): string =>
    `/!api/internal/repositories/${owner}/${repoSlug}/pullrequests/${pullRequestId}/labels`,

  // pull request label
  prLabel: (
    owner: string,
    repoSlug: string,
    pullRequestId: string | number,
    label: string
  ): string =>
    `/!api/internal/repositories/${owner}/${repoSlug}/pullrequests/${pullRequestId}/labels/${label}`,

  // pull request labels list
  labels: (owner: string): string =>
    `/!api/internal/workspaces/${owner}/pullrequest-labels`,

  pullRequestQuickUpdate: (
    owner: string,
    repoSlug: string,
    pullRequestId: string | number
  ) =>
    `/!api/internal/repositories/${owner}/${repoSlug}/pullrequests/${pullRequestId}/quickupdate/`,
};
